import { DISTANCE_UNITS, getAllDistanceUnits } from "form/map/model"
import {
  Feature,
  FeatureCollection,
  Feature as GeoFeature,
  Geometry,
  LineString,
  MultiLineString,
  MultiPolygon,
  Point,
  Polygon
} from "geojson"
import {
  GEOMETRY_UNITS,
  getAllGeometryTypes,
  IBoundaryBox,
  ICircle,
  IGeometryService,
  ILine,
  ILocation,
  IPoint,
  IPolygon
} from "geometry/model"
import { parse as parseWkt } from "wellknown"
import {
  areCoordinatesEquals,
  featureIntersect,
  getFeature,
  getIBoundaryBox,
  getPointFromLocation
} from "./tools/common"

// Do not put in a service state, it's just for loading the lib asynchronously
let turf: typeof import("@turf/turf")

// Async load of turf lib, in order to lower startup bundle load time
import(/* webpackChunkName: "turf" */ "@turf/turf")
  .then(importedFile => {
    turf = importedFile
  })
  .catch(error => {
    console.error("An error occurred while async loading turf", error)
  })

export function getTurf(): typeof turf {
  return turf
}

export const geometrySVC: IGeometryService = {
  checkLocation(location: ILocation): void {
    if (!location) {
      throw Error("The location is undefined")
    }
    if (typeof location !== "object") {
      throw Error("The location is not an object")
    }
    if (typeof location.x !== "number") {
      throw Error("The location 'x' is not a number")
    }
    if (typeof location.y !== "number") {
      throw Error("The location 'y' is not a number")
    }
  },
  isValidLocation(location: ILocation | undefined): boolean {
    return (
      location !== undefined &&
      typeof location === "object" &&
      typeof location.x === "number" &&
      typeof location.y === "number"
    )
  },
  isValidBbox(bbox: IBoundaryBox | undefined): boolean {
    if (!bbox || typeof bbox !== "object") {
      return false
    }
    return this.isValidLocation(bbox.sw) && this.isValidLocation(bbox.ne)
  },
  isValidGeometry(geometry: any): boolean {
    if (typeof geometry !== "object") {
      return false
    }
    if (getAllGeometryTypes().indexOf(geometry.type) === -1) {
      return false
    }
    if (!Array.isArray(geometry.coordinates)) {
      return false
    }
    return true
  },
  checkCircle(circle: ICircle): void {
    if (!circle) {
      throw Error("The circle is undefined")
    }
    if (typeof circle !== "object") {
      throw Error("The circle is not an object")
    }
    if (typeof circle.radius !== "number") {
      throw Error("The circle 'radius' is not a number")
    }
    this.checkLocation(circle.center)
  },
  checkPolygon(polygon: IPolygon): void {
    if (!polygon) {
      throw Error("The polygon is undefined")
    }
    if (!Array.isArray(polygon)) {
      throw Error("The polygon is not an array")
    }
    if (polygon.length < 4) {
      throw Error("The polygon length is lower to 4")
    }
  },
  checkLine(line: ILine): void {
    if (!line) {
      throw Error("The line is undefined")
    }
    if (!Array.isArray(line)) {
      throw Error("The line is not an array")
    }
    if (line.length < 2) {
      throw Error("The line length is lower to 2")
    }
  },
  checkBbox(bbox: IBoundaryBox): void {
    if (!bbox) {
      throw Error("The boundary box parameter is missing")
    }
    if (typeof bbox !== "object") {
      throw Error("The boundary box parameter is not an object")
    }
    this.checkLocation(bbox.sw)
    this.checkLocation(bbox.ne)
  },
  getArea(feature: GeoFeature): number {
    const geometry: Geometry = feature.geometry as Geometry
    if (geometry.type === "Polygon") {
      return turf.area(turf.polygon((feature.geometry as any).coordinates))
    } else if (geometry.type === "MultiPolygon") {
      return turf.area(turf.flatten(turf.multiPolygon((feature.geometry as any).coordinates)))
    }
    throw Error(`Cannot get area for geometry type = "${geometry.type}"`)
  },
  getLineLength(feature: Feature<LineString> | Feature<MultiLineString>, units?: GEOMETRY_UNITS): number {
    if (!units) {
      units = GEOMETRY_UNITS.KILOMETERS
    }
    const geometry: LineString = feature.geometry as LineString
    if (geometry.coordinates.length < 2) {
      return 0
    }
    if (geometry.type === "LineString") {
      return turf.length(turf.lineString((feature.geometry as any).coordinates), { units })
    } else if (geometry.type === "MultiLineString") {
      return turf.length(turf.flatten(turf.multiLineString((feature.geometry as any).coordinates)), { units })
    }
    throw Error(`Cannot get line length for geometry type = "${geometry.type}"`)
  },
  getCentroid(feature: GeoFeature | FeatureCollection): GeoFeature<Point> {
    if (feature.type === "FeatureCollection") {
      return turf.centroid(feature as any)
    }
    const geometry: Geometry = feature.geometry as Geometry
    if (geometry.type === "Polygon") {
      return turf.centroid(turf.polygon((feature.geometry as any).coordinates))
    } else if (geometry.type === "MultiPolygon") {
      return turf.centroid(turf.flatten(turf.multiPolygon((feature.geometry as any).coordinates)))
    } else if (geometry.type === "LineString") {
      return turf.centroid(turf.lineString((feature.geometry as any).coordinates))
    } else if (geometry.type === "MultiLineString") {
      return turf.centroid(turf.multiLineString((feature.geometry as any).coordinates))
    } else if (geometry.type === "Point") {
      return turf.centroid(turf.point((feature.geometry as any).coordinates))
    } else if (geometry.type === "MultiPoint") {
      return turf.centroid(turf.multiPoint((geometry as any).coordinates))
    }
    throw Error(`Cannot get centroid for geometry type = "${geometry.type}"`)
  },
  getFeatureFromLine(line: ILine): GeoFeature<LineString> {
    this.checkLine(line)
    const lineFeature = turf.lineString(line)
    if (!lineFeature) {
      throw Error("Cannot get turf line feature from jmap line")
    }
    return lineFeature
  },
  getFeatureFromWkt(wkt: string): GeoFeature {
    if (typeof wkt !== "string") {
      throw Error("wkt param must be a string")
    }
    const geometry = parseWkt(wkt)
    if (geometry === null) {
      console.error("Cannot parse WKT", wkt)
      throw Error("Invalid Well-Known Text")
    }
    return {
      geometry,
      properties: {},
      type: "Feature"
    }
  },
  getPolygonFeatureFromCircle(circle: ICircle, units?: GEOMETRY_UNITS): GeoFeature<Polygon> {
    this.checkCircle(circle)
    if (!units) {
      units = GEOMETRY_UNITS.KILOMETERS
    }
    const center: ILocation = circle.center
    const radius: number = circle.radius
    const polygon = turf.circle([center.x, center.y], radius, { steps: 200, units })
    if (!polygon) {
      throw Error("Cannot get turf polygon feature from jmap circle")
    }
    return polygon as any
  },
  getFeatureFromPolygon(polygon: IPolygon): GeoFeature<Polygon> {
    this.checkPolygon(polygon)
    const polygonFeature = turf.polygon([polygon])
    if (!polygonFeature) {
      throw Error("Cannot get turf polygon feature from jmap polygon")
    }
    return polygonFeature as any
  },
  getBboxFromFeature(feature: GeoFeature): IBoundaryBox {
    if (!feature) {
      throw Error("Missing parameter feature")
    }
    return getIBoundaryBox(turf.bbox(feature as Feature))
  },
  getBboxFromFeatures(features: GeoFeature[]): IBoundaryBox {
    return getIBoundaryBox(turf.bbox(turf.combine(this.getFeatureCollection(features) as any)))
  },
  getBboxFromPolygon(polygon: IPolygon): IBoundaryBox {
    return this.getBboxFromFeature(this.getFeatureFromPolygon(polygon))
  },
  getBboxFromLine(line: ILine): IBoundaryBox {
    return this.getBboxFromFeature(this.getFeatureFromLine(line))
  },
  getPolygonFeatureFromBbox(boundaryBox: IBoundaryBox): GeoFeature<Polygon> {
    this.checkBbox(boundaryBox)
    return turf.bboxPolygon([boundaryBox.sw.x, boundaryBox.sw.y, boundaryBox.ne.x, boundaryBox.ne.y])
  },
  bboxIntersect(bbox1: IBoundaryBox, bbox2: IBoundaryBox): boolean {
    return Boolean(turf.intersect(this.getPolygonFeatureFromBbox(bbox1), this.getPolygonFeatureFromBbox(bbox2)))
  },
  polygonIntersect(f1: GeoFeature<Polygon>, f2: GeoFeature): boolean {
    return featureIntersect(turf, f1, f2)
  },
  lineIntersect(f1: GeoFeature<LineString>, f2: GeoFeature): boolean {
    return featureIntersect(turf, f1, f2)
  },
  getDistance(p1: IPoint | ILocation, p2: IPoint | ILocation): number {
    return turf.distance(getPointFromLocation(p1), getPointFromLocation(p2))
  },
  getFeatureCollection(features: GeoFeature[] | ILocation[] | IPoint[]): FeatureCollection {
    return turf.featureCollection((features as []).map(f => getFeature(f))) as FeatureCollection
  },
  getCircleFeature(center: IPoint | ILocation, radius: number, units: DISTANCE_UNITS): GeoFeature<Polygon> {
    return turf.circle(getPointFromLocation(center), radius, units ? { units } : undefined) as GeoFeature<Polygon>
  },
  getPolygonFeature(coordinates: IPoint[], properties: any, closeCoordinates?: boolean): GeoFeature<Polygon> {
    if (!coordinates || coordinates.length < 3) {
      throw Error("Coordinates length is lower than 3")
    }
    let finalCoordinates: IPoint[] = coordinates
    if (closeCoordinates && !areCoordinatesEquals(coordinates[0], coordinates[coordinates.length - 1])) {
      finalCoordinates = coordinates.slice()
      finalCoordinates.push(coordinates[0])
    }
    return turf.polygon([finalCoordinates], properties) as GeoFeature<Polygon>
  },
  getMultiPolygonFeature(geometries: Array<Polygon | MultiPolygon>, properties: any): Feature<MultiPolygon> {
    if (!Array.isArray(geometries)) {
      throw Error("polygons is not an array")
    }
    const multiPolygons: MultiPolygon[] = []
    const polygons: Polygon[] = []
    for (const geometry of geometries) {
      if (geometry.type === "MultiPolygon") {
        multiPolygons.push(geometry)
      } else {
        polygons.push(geometry)
      }
    }
    const multiPolygonFeature = turf.multiPolygon(
      polygons.map(p => p.coordinates),
      properties
    )
    for (const multiPolygon of multiPolygons) {
      multiPolygonFeature.geometry.coordinates.push(...multiPolygon.coordinates)
    }
    return multiPolygonFeature
  },
  getRotatedFeature(feature: GeoJSON.Feature, angle: number): GeoJSON.Feature {
    if (!feature || typeof feature !== "object") {
      throw Error("Feature is not a valid feature")
    }
    if (isNaN(angle)) {
      throw Error("Angle must be a number")
    }
    return turf.transformRotate(feature as any, angle) as GeoJSON.Feature
  },
  convertLength(length: number, toUnit: DISTANCE_UNITS, fromUnit?: DISTANCE_UNITS): number {
    if (typeof length !== "number") {
      throw Error("Length is not a number")
    }
    const allDistanceUnits = getAllDistanceUnits()
    if (!allDistanceUnits.includes(toUnit)) {
      throw Error(`To unit not found. Available units: ${allDistanceUnits.join(", ")}`)
    }
    if (fromUnit !== undefined && !allDistanceUnits.includes(toUnit)) {
      throw Error(`From unit not found. Available units: ${allDistanceUnits.join(", ")}`)
    }
    if (!fromUnit) {
      fromUnit = DISTANCE_UNITS.KILOMETERS
    }
    if (fromUnit === toUnit) {
      return length
    }
    return turf.convertLength(length, fromUnit, toUnit)
  },
  convertArea(area: number, toUnit: DISTANCE_UNITS, fromUnit?: DISTANCE_UNITS): number {
    if (typeof area !== "number") {
      throw Error("Area is not a number")
    }
    const allDistanceUnits = getAllDistanceUnits()
    if (!allDistanceUnits.includes(toUnit)) {
      throw Error(`To unit not found. Available units: ${allDistanceUnits.join(", ")}`)
    }
    if (fromUnit !== undefined && !allDistanceUnits.includes(toUnit)) {
      throw Error(`From unit not found. Available units: ${allDistanceUnits.join(", ")}`)
    }
    if (!fromUnit) {
      fromUnit = DISTANCE_UNITS.KILOMETERS
    }
    if (fromUnit === toUnit) {
      return area
    }
    return turf.convertArea(area, fromUnit, toUnit)
  },
  getDestination(origin: ILocation, distance: number, bearing: number): Feature<Point> {
    return turf.destination([origin.x, origin.y], distance, bearing, { units: "meters" })
  }
}
